'use client';

import { AppImageClient, AppImageData } from '@components/appImage';
import { Card } from '@components/card';
import { Typography } from '@components/typography';
import { classNames } from '@lib/classNames/classNames';
import { cn } from '@lib/utils/cn';
import { useState } from 'react';
import type { Swiper as SwiperType } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

export interface DataSlide {
  id: number;
  tab: string;
  imageKey: string;
  icon: React.ReactNode;
  title?: string;
  titleMaxWidth?: number;
  description?: string;
  slideDescriptionPlacement?: 'bottomLeft' | 'bottomRight';
}

export interface DataTypesSliderProps {
  slides: DataSlide[];
  /** Данные AppImage по ключу из `getAppImagesData`. */
  imagesDataMap?: Record<string, AppImageData>;
  imageContainerHeight?: number;
  customSliderClasses?: string;
}

export const DataTypesSlider: React.FC<DataTypesSliderProps> = ({
  slides,
  imagesDataMap,
  customSliderClasses,
  imageContainerHeight = 664,
}) => {
  const [activeSlide, setActiveSlide] = useState(0);
  const [swiperInstance, setSwiperInstance] = useState<SwiperType | null>(null);

  const handleSlideChange = (swiper: SwiperType) => {
    setActiveSlide(swiper.activeIndex);
  };

  const handleChipClick = (index: number) => () => {
    setActiveSlide(index);
    swiperInstance?.slideTo(index);
  };

  const imageSrc = slides.find((s) => s.id === activeSlide);
  const activeImageData = imageSrc && imagesDataMap?.[imageSrc.imageKey];

  return (
    <>
      {/** Блок для мобильных */}
      <div className="flex flex-col gap-4 md:hidden">
        {/* Swiper с табами-чипсами */}
        <Swiper
          spaceBetween={12}
          slidesPerView={2}
          onSwiper={setSwiperInstance}
          onSlideChange={handleSlideChange}
          className={classNames({
            cls: 'min-h-[120px] w-full',
            additional: [customSliderClasses],
          })}
        >
          {slides.map((slide, index) => {
            const isActive = activeSlide === index;
            return (
              <SwiperSlide key={slide.id} className="max-w-[160px]">
                <button
                  onClick={handleChipClick(index)}
                  className={classNames({
                    cls: 'flex size-full flex-col items-start justify-between gap-3 rounded-[20px] p-4 pb-3 text-start text-white transition-all duration-300',
                    mods: {
                      'bg-surface-base-inverse-primary': isActive,
                      'cursor-pointer': !isActive,
                      'bg-surface-base-secondary': !isActive,
                      'hover:bg-surface-base-blur': !isActive,
                    },
                  })}
                >
                  <Typography variant="text-m">{slide.tab}</Typography>
                  <div className="rounded-8 bg-surface-base-blur flex h-[32px] w-[32px] items-center justify-center">
                    {slide.icon}
                  </div>
                </button>
              </SwiperSlide>
            );
          })}
        </Swiper>

        {/* Блок с картинкой активного слайда */}
        <div
          className={classNames({
            additional: [customSliderClasses],
          })}
        >
          <Card rounded="20">
            <div className="flex flex-col gap-4 p-2 pb-5">
              {activeImageData && (
                <AppImageClient
                  key={imageSrc.imageKey}
                  {...activeImageData}
                  alt={slides[activeSlide].tab}
                  className="h-full w-full rounded-[12px] object-cover transition-opacity duration-300"
                />
              )}

              {/* Описание поверх картинки */}
              <div className="bg-surface-base-secondary flex max-w-[360px] flex-col gap-2 rounded-[16px] px-3">
                <Typography variant="heading-s">
                  {slides[activeSlide]?.title}
                </Typography>
                {slides[activeSlide]?.description && (
                  <Typography variant="text-base">
                    {slides[activeSlide].description}
                  </Typography>
                )}
              </div>
            </div>
          </Card>
        </div>
      </div>
      {/** Блок для браузеров */}
      <div className="flex flex-col gap-4 max-sm:hidden">
        {/* Swiper с табами-чипсами */}
        <Swiper
          spaceBetween={16}
          slidesPerView={slides.length}
          onSwiper={setSwiperInstance}
          onSlideChange={handleSlideChange}
          className="min-h-[130px] w-full"
        >
          {slides.map((slide, index) => {
            const isActive = activeSlide === index;
            return (
              <SwiperSlide key={slide.id}>
                <button
                  onClick={handleChipClick(index)}
                  className={classNames({
                    cls: 'flex size-full flex-col items-start justify-between gap-4 rounded-[16px] p-4 pb-3 text-start text-white transition-all duration-300',
                    mods: {
                      'bg-surface-base-inverse-primary': isActive,
                      'cursor-pointer': !isActive,
                      'bg-surface-base-secondary': !isActive,
                      'hover:bg-surface-base-blur': !isActive,
                    },
                  })}
                >
                  <span className="text-[16px] leading-[24px]">
                    {slide.tab}
                  </span>
                  <div className="rounded-8 bg-surface-base-blur flex h-[32px] w-[32px] items-center justify-center">
                    {slide.icon}
                  </div>
                </button>
              </SwiperSlide>
            );
          })}
        </Swiper>

        {/* Блок с картинкой активного слайда */}
        <div
          className={
            'bg-surface-base-blur relative overflow-hidden rounded-4xl p-3'
          }
          style={{ height: imageContainerHeight }}
        >
          {activeImageData && (
            <AppImageClient
              key={imageSrc.imageKey}
              {...activeImageData}
              alt={slides[activeSlide].tab}
              /** - 24 потому что на родителе стоит p-3 */
              style={{ height: imageContainerHeight - 24 }}
              className="w-full rounded-[20px] object-cover transition-opacity duration-300"
            />
          )}
          {/* Описание поверх картинки */}
          <div
            className={cn(
              'absolute',
              PLACEMENT_STYLES[
                slides[activeSlide]?.slideDescriptionPlacement ?? 'bottomLeft'
              ],
            )}
          >
            <div
              className="bg-surface-base-secondary flex flex-col gap-2 rounded-[16px] px-6 py-5"
              style={{
                maxWidth: slides[activeSlide]?.titleMaxWidth ?? 360,
              }}
            >
              {slides[activeSlide]?.title && (
                <Typography variant="heading-s">
                  {slides[activeSlide]?.title}
                </Typography>
              )}
              {slides[activeSlide]?.description && (
                <Typography variant="text-base">
                  {slides[activeSlide].description}
                </Typography>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const PLACEMENT_STYLES = {
  bottomLeft: 'bottom-8 left-8',
  bottomRight: 'bottom-8 right-8',
};
